/**
 * This module contains the logic for managing asset uploads within the editor.
 */

import type { Minidoc } from 'client/lib/minidoc';
import { makeUploader } from 'client/lib/rpx-client/uploader';
import { isOverUploadLimit, oversizeMessage } from 'client/lib/upload-limits';
import { captureException } from 'client/lib/sentry';
import { CardRenderOptions } from 'minidoc-editor';
import { Changeable } from 'minidoc-editor/dist/types/undo-redo';
import { Align } from 'shared/media';
import { baseUrl } from 'shared/urls';

/**
 * The shape of the JSON embedded in the media-card.
 */
export interface MediaState {
  /**
   * The ID of the file. It is set when the upload is complete.
   */
  fileId?: UUID;

  /**
   * The URL to the media, proxied through our API layer
   * to allow future security / updates, etc.
   */
  url: string;

  /**
   * The URL to the poster image, if any.
   */
  poster?: string;

  /**
   * The type of media, e.g. image/png, video/mp4
   */
  type: string;

  /**
   * The name of the file.
   */
  name: string;

  /**
   * The aspect ratio. This is a percentage of the height / width,
   * and is used to keep the document from bouncing as images /
   * video load.
   */
  ratio?: number;

  /**
   * This is the width of the asset, used to ensure the ratio is
   * applied to the correct width, rather than to the full document
   * width. (e.g. images which are narrower than the document)
   */
  width?: number;

  /**
   * The caption, if any, displayed beneath the media.
   */
  caption?: string;

  /**
   * If set, this is the hyperlink the image will be wrapped in.
   */
  href?: string;

  /**
   * If set, this is the size the user has specified, overriding width.
   * It is a percent, for example, 120, 100, 50, 25
   */
  size?: string;

  /**
   * If set, this is the alignment (left, center, right).
   */
  align?: Align;

  /**
   * If true, this allows users download the media.
   * This is only supported for PDFs right now.
   */
  downloadable?: boolean;

  /*
   * If true, this media has a caption file associated with it.
   */
  hasCaptions?: boolean;
}

export type RenderOpts = CardRenderOptions<MediaState> & {
  isPublic: boolean;
  cdnWidth?: number;
  tmpUrl?: string;
  hideCaptions?: boolean;
  studentEditor?: boolean;
  onRetry: () => void;
};

/**
 * The handler for an upload progress notification.
 */
type ProgressHandler = (progress: number) => void;

/**
 * This is the data required to track asset upload progress and the asset file
 * across undo / redo operations.
 */
export interface UploadState {
  /**
   * The media state associated with this upload. This allows us
   * to have a stable undo / redo.
   */
  mediaState: MediaState;
  /**
   * The promise that will resolve when the upload completes.
   */
  promise: Promise<any>;
  /**
   * The file being uploaded.
   */
  file: File;
  /**
   * Indicates whether or not the upload is complete.
   */
  isUploading: boolean;
  /**
   * Retry the upload if it failed.
   */
  retry(): Promise<unknown>;
  /**
   * Indicate that the upload was interrupted or failed, and needs retry
   */
  isFailed?: boolean;
  /**
   * Register a function to get notified of upload progress. This returns the
   * unregister / off function.
   */
  onProgress?: (fn: ProgressHandler) => () => void;
  /**
   * The current progress.
   */
  progress: number;
  error?: string;
  canRetry: boolean;
}

type MakeUploader = (opts: Parameters<typeof makeUploader>[0]) => {
  upload(): Promise<{ url: string } | undefined>;
};

const uploadFailedMessage = 'Check your connection, then retry.';

let uploadSeq = 0;

/**
 * Create an object that tracks editor file uploads. This allows upload
 * status and other info to be stable across undo /redo operations.
 */
export class MediaUploader {
  editor: Minidoc;

  uploads = {} as Record<string, UploadState | undefined>;

  createUploader: MakeUploader;

  constructor(editor: Minidoc, createUploader: MakeUploader = makeUploader) {
    this.editor = editor;
    this.createUploader = createUploader;
  }

  /**
   * Get the upload state associated with the specified URL.
   */
  getUploadState(url: string): UploadState | undefined {
    return this.uploads[url];
  }

  /**
   * Create the upload state, and begin the upload.
   */
  createUploadState({
    file,
    isPublic,
    downloadable,
    domain,
    maxSize,
  }: {
    file: File;
    isPublic: boolean;
    downloadable?: boolean;
    domain?: string;
    maxSize?: number;
  }): UploadState {
    const progressHandlers = new Set<ProgressHandler>();
    const tmpUrl = `tmp:${Date.now()}-${++uploadSeq}-${file.name}`;
    const mediaState: MediaState = {
      url: tmpUrl,
      name: file.name,
      type: file.type,
      downloadable,
    };
    let latestProgress = 0;
    const uploader = this.createUploader({
      file,
      isPublic,
      onProgress({ progress }) {
        latestProgress = progress;
        progressHandlers.forEach((fn) => {
          fn(progress);
        });
      },
    });
    const fail = (err: any) => {
      uploadState.isUploading = false;
      uploadState.isFailed = true;
      if (err?.data?.type === 'maxSizeExceeded') {
        uploadState.canRetry = false;
        uploadState.error = err.message;
      } else {
        uploadState.canRetry = true;
        uploadState.error = uploadFailedMessage;
      }
      captureException(new Error('Failed to upload file'), {
        size: file.size,
        type: file.type,
        message: err?.message,
      });
      return Promise.reject(err);
    };
    const upload = async () => {
      const result = await uploader.upload();
      if (!result) {
        throw new Error(`Upload failed.`);
      }
      mediaState.url = domain ? new URL(result.url, baseUrl({ domain })).href : result.url;
      uploadState.isUploading = false;
      uploadState.isFailed = false;
      uploadState.error = undefined;
      uploadState.onProgress = undefined;
      (this.editor as Changeable).onChange();
    };
    const start = () => {
      const promise = upload().catch(fail);
      promise.catch(() => {});
      return promise;
    };
    const uploadState: UploadState = {
      file,
      mediaState,
      isFailed: false,
      isUploading: true,
      canRetry: true,
      get progress() {
        return latestProgress;
      },
      onProgress(fn) {
        progressHandlers.add(fn);
        return () => progressHandlers.delete(fn);
      },
      promise: Promise.resolve(),
      retry() {
        if (uploadState.isUploading || !uploadState.canRetry) {
          return uploadState.promise;
        }
        latestProgress = 0;
        uploadState.isUploading = true;
        uploadState.isFailed = false;
        uploadState.error = undefined;
        uploadState.promise = start();
        return uploadState.promise;
      },
    };

    if (maxSize !== undefined && isOverUploadLimit(file, maxSize)) {
      uploadState.isUploading = false;
      uploadState.isFailed = true;
      uploadState.canRetry = false;
      uploadState.error = oversizeMessage(file, maxSize);
      uploadState.promise = Promise.reject(new Error(uploadState.error));
      uploadState.promise.catch(() => {});
    } else {
      uploadState.promise = start();
    }

    this.uploads[tmpUrl] = uploadState;

    return uploadState;
  }
}

/**
 * Get the media upload tracker for the specified editor.
 */
export function mediaUploader(editor: any): MediaUploader {
  if (!editor._uploader) {
    editor._uploader = new MediaUploader(editor);
  }
  return editor._uploader;
}

/**
 * Get the number of pending uploads.
 */
export function uploadCount(editor: any) {
  return Object.values(mediaUploader(editor).uploads).filter((v) => !!v && v.isUploading).length;
}

export type PendingMedia = {
  uploading: number;
  failed: number;
  recording: boolean;
};

export function trackUploadElement(el: HTMLElement, upload: UploadState) {
  (el as any).$upload = upload;
}

export function setRecording(editor: any, recording: boolean) {
  editor.$recording = recording;
  editor.root.dispatchEvent(new CustomEvent('mini:media', { bubbles: true }));
}

export function pendingMedia(editor: any): PendingMedia {
  const pending: PendingMedia = { uploading: 0, failed: 0, recording: !!editor?.$recording };
  editor?.root.querySelectorAll('.media-card').forEach((el: any) => {
    const upload: UploadState | undefined = el.$upload;
    if (upload?.isUploading) {
      pending.uploading++;
    } else if (upload?.isFailed) {
      pending.failed++;
    }
  });
  return pending;
}

export function hasPendingMedia({ uploading, failed, recording }: PendingMedia) {
  return uploading > 0 || failed > 0 || recording;
}
